import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Trash2,
  Maximize2,
  X,
  Copy,
  Check,
  ExternalLink,
  QrCode as QrIcon,
} from "lucide-react";
import { staffApi } from "@client/api/staff";
import { StaffShell } from "@client/components/StaffShell";
import { QrCode } from "@client/components/QrCode";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import { useConfirm } from "@client/components/confirm-dialog";
import {
  sessionStatusLabel,
  sessionStatusBadgeClass,
} from "@client/lib/session-status";
import {
  attemptStatusLabel,
  attemptStatusBadgeClass,
} from "@client/lib/attempt-status";

function slugify(text: string): string {
  const cleaned = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return cleaned || "session";
}

export const Route = createFileRoute("/_authenticated/sessions/$sessionId")({
  component: SessionDetail,
});

function SessionDetail() {
  const { sessionId } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [origin, setOrigin] = useState("");
  const [copied, setCopied] = useState(false);
  const [useFriendlyUrl, setUseFriendlyUrl] = useState(true);
  const [showPresentation, setShowPresentation] = useState(() => {
    if (typeof window !== "undefined") {
      return (
        new URLSearchParams(window.location.search).get("present") === "true"
      );
    }
    return false;
  });

  useEffect(() => setOrigin(window.location.origin), []);

  const { data } = useQuery({
    queryKey: ["session", sessionId],
    refetchInterval: 4000,
    queryFn: () => staffApi.session(sessionId),
  });
  const session = data?.session;
  const attempts = data?.attempts;

  const openMut = useMutation({
    mutationFn: async () => staffApi.openSession(sessionId),
    onSuccess: () => {
      toast.success("Session opened — referees can now join");
      qc.invalidateQueries({ queryKey: ["session", sessionId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const startMut = useMutation({
    mutationFn: async () => staffApi.startSession(sessionId),
    onSuccess: () => {
      toast.success("Examination started for all present referees");
      qc.invalidateQueries({ queryKey: ["session", sessionId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const endMut = useMutation({
    mutationFn: async () => staffApi.endSession(sessionId),
    onSuccess: () => {
      toast.success("Session ended and all attempts graded");
      qc.invalidateQueries({ queryKey: ["session", sessionId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: async () => staffApi.deleteSession(sessionId),
    onSuccess: () => {
      toast.success("Session deleted successfully");
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      navigate({ to: "/sessions" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const terminateMut = useMutation({
    mutationFn: staffApi.terminateAttempt,
    onSuccess: () => {
      toast.success("Attempt terminated");
      qc.invalidateQueries({ queryKey: ["session", sessionId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const exam = session?.exams as {
    name: string;
    time_per_question: number;
    passing_score: number;
    exam_questions: { count: number }[];
  } | null;

  const rawJoinUrl =
    session && origin ? `${origin}/s/${session.access_token}` : "";
  const slugToken = session
    ? session.access_token.includes("exam-")
      ? session.access_token
      : `exam-${slugify(exam?.name ?? "exam")}-session-${slugify(session.title)}-${session.access_token.slice(0, 6)}`
    : "";
  const friendlyJoinUrl = session && origin ? `${origin}/s/${slugToken}` : "";
  const joinUrl = useFriendlyUrl ? friendlyJoinUrl : rawJoinUrl;

  return (
    <StaffShell
      title={session?.title ?? "Session"}
      subtitle={exam?.name}
      showBack
      actions={
        <div className="flex flex-wrap gap-2">
          {session ? (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => setShowPresentation(true)}
            >
              <Maximize2 className="h-4 w-4 text-primary" />
              Display QR Code
            </Button>
          ) : null}
          {session?.status === "DRAFT" ? (
            <Button
              size="sm"
              onClick={() => openMut.mutate()}
              disabled={openMut.isPending}
            >
              Open for joining
            </Button>
          ) : null}
          {session?.status === "WAITING" ? (
            <Button
              size="sm"
              onClick={() => startMut.mutate()}
              disabled={startMut.isPending}
            >
              Start examination
            </Button>
          ) : null}
          {session &&
          (session.status === "WAITING" || session.status === "ACTIVE") ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => endMut.mutate()}
              disabled={endMut.isPending}
            >
              End session
            </Button>
          ) : null}
          {session ? (
            <Button
              size="sm"
              variant="destructive"
              className="gap-1 text-xs"
              onClick={async () => {
                if (
                  await confirm({
                    title: "Delete session?",
                    description: `Delete session "${session.title}"? This will also remove associated referee attempts.`,
                    confirmText: "Delete",
                    destructive: true,
                  })
                ) {
                  deleteMut.mutate();
                }
              }}
              disabled={deleteMut.isPending}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete session
            </Button>
          ) : null}
        </div>
      }
    >
      {/* Presentation Fullscreen Modal */}
      {showPresentation && (
        <div className="fixed inset-0 z-50 flex flex-col bg-slate-950 text-white animate-in fade-in duration-200 overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-800 bg-slate-900/90 px-6 py-4 backdrop-blur-md">
            <div>
              <h1 className="hall-heading text-xl font-bold tracking-wide text-white">
                {session?.title ?? "Session Entry"}
              </h1>
              <p className="text-xs text-slate-400">
                {exam?.name} · Passing score: {exam?.passing_score}%
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge
                variant="outline"
                className={sessionStatusBadgeClass(session?.status)}
              >
                {sessionStatusLabel(session?.status)}
              </Badge>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-slate-400 hover:bg-slate-800 hover:text-white cursor-pointer"
                onClick={() => setShowPresentation(false)}
                title="Close Presentation View"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Main Display Body */}
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="max-w-2xl w-full space-y-6 flex flex-col items-center">
              {/* QR Code Container */}
              <div className="rounded-2xl border-2 border-slate-700 bg-white p-6 shadow-2xl transition-transform hover:scale-105">
                {joinUrl ? <QrCode value={joinUrl} size={320} /> : null}
              </div>

              {/* URL Display */}
              <div className="w-full space-y-3">
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">
                  Examination Entry Link
                </p>
                <div className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900/90 px-5 py-4 shadow-inner">
                  <span className="font-mono text-base sm:text-lg text-blue-400 font-semibold break-all select-all">
                    {joinUrl}
                  </span>
                </div>

                <div className="flex flex-wrap justify-center gap-3 pt-2">
                  <Button
                    size="lg"
                    className="gap-2 font-semibold cursor-pointer"
                    onClick={() => {
                      navigator.clipboard.writeText(joinUrl);
                      setCopied(true);
                      toast.success("Entry link copied to clipboard");
                      setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    {copied ? (
                      <Check className="h-5 w-5 text-green-400" />
                    ) : (
                      <Copy className="h-5 w-5" />
                    )}
                    {copied ? "Link Copied!" : "Copy Entry Link"}
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="gap-2 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white cursor-pointer"
                    onClick={() => window.open(joinUrl, "_blank")}
                  >
                    <ExternalLink className="h-5 w-5" />
                    Open Entry Page
                  </Button>
                  <Button
                    size="lg"
                    variant="ghost"
                    className="gap-2 text-xs text-slate-400 hover:bg-slate-800 hover:text-white cursor-pointer"
                    onClick={() => setUseFriendlyUrl((v) => !v)}
                  >
                    {useFriendlyUrl
                      ? "Format: Friendly Slug"
                      : "Format: Token ID"}
                  </Button>
                </div>
              </div>

              {/* Referees Joined Counter */}
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900 px-4 py-2 text-sm text-slate-300">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                <span>
                  Referees Joined: <strong>{attempts?.length ?? 0}</strong>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="panel space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h2 className="hall-heading text-lg font-bold">Hall entry</h2>
            <Badge
              variant="outline"
              className={sessionStatusBadgeClass(session?.status)}
            >
              {sessionStatusLabel(session?.status)}
            </Badge>
          </div>
          {joinUrl ? <QrCode value={joinUrl} /> : null}
          <p className="break-all rounded-md bg-secondary p-3 text-xs text-muted-foreground">
            {joinUrl}
          </p>
          <div className="space-y-2">
            <Button
              variant="default"
              size="sm"
              className="w-full gap-1.5"
              onClick={() => setShowPresentation(true)}
            >
              <Maximize2 className="h-4 w-4" />
              Project / Fullscreen View
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                navigator.clipboard.writeText(joinUrl);
                toast.success("Entry link copied");
              }}
            >
              Copy entry link
            </Button>
          </div>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Questions</dt>
              <dd>{exam?.exam_questions?.[0]?.count ?? 0}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Time / question</dt>
              <dd>{exam?.time_per_question}s</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Passing score</dt>
              <dd>{exam?.passing_score}%</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Started</dt>
              <dd>
                {session?.started_at
                  ? new Date(session.started_at).toLocaleTimeString()
                  : "—"}
              </dd>
            </div>
          </dl>
          {session?.status === "WAITING" ? (
            <p className="text-xs text-muted-foreground">
              Referees who join after the start are refused entry.
            </p>
          ) : null}
        </div>

        <div className="panel p-5">
          <h2 className="hall-heading text-lg font-bold">
            Referees ({attempts?.length ?? 0})
          </h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2">Name</th>
                  <th>Version</th>
                  <th>Country</th>
                  <th>City</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th>Result</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(attempts ?? []).map((a) => {
                  const r = (
                    a.results as unknown as {
                      percentage: number;
                      passed: boolean;
                    }[]
                  )?.[0];
                  return (
                    <tr key={a.id} className="border-t border-border">
                      <td className="py-2 font-medium">{a.full_name}</td>
                      <td>
                        {a.version_label ? (
                          <Badge variant="outline" className="font-semibold">
                            Version {a.version_label}
                          </Badge>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{a.country}</td>
                      <td>{a.geo_city ?? "—"}</td>
                      <td>
                        {r ? (
                          <span className="font-semibold">
                            {Number(r.percentage).toFixed(1)}%
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <Badge
                          variant="outline"
                          className={attemptStatusBadgeClass(a.status)}
                        >
                          {attemptStatusLabel(a.status)}
                        </Badge>
                      </td>
                      <td>
                        {r ? (
                          <Badge variant={r.passed ? "default" : "destructive"}>
                            {r.passed ? "PASS" : "FAIL"}
                          </Badge>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="text-right">
                        {a.status === "ACTIVE" ||
                        a.status === "WAITING" ||
                        a.status === "JOINED" ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => terminateMut.mutate(a.id)}
                            disabled={terminateMut.isPending}
                          >
                            Terminate
                          </Button>
                        ) : (
                          <Link
                            to="/results/$attemptId"
                            params={{ attemptId: a.id }}
                            className="text-xs underline"
                          >
                            View
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!attempts?.length ? (
              <p className="mt-3 text-sm text-muted-foreground">
                No referees have joined yet.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </StaffShell>
  );
}
